import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { AppService } from 'src/app/app.service';
import { PhotographerFilterService } from 'src/app/services/filters/photographer-filter.service';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { Order, OrderValitation, OrderStatus, OrderDetail, ColorShots, OrderTypeEnum } from '../../../models/new/order';
import { OrderMapper } from '../../../services/order-mapper';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { Color, Style } from '../../../models/new/style';
import { SelectedStylesModalComponent } from '../selected-styles-modal/selected-styles-modal.component';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Component({
	selector: 'bt-create-order-modal',
	standalone: false,
	templateUrl: './create-order-modal.component.html',
	styleUrl: './create-order-modal.component.scss'
})
export class CreateOrderModalComponent {
	order: Order = new Order({ creationDate: new Date() });
	orderValidation: OrderValitation = new OrderValitation();

	orderStatus = OrderStatus;
	loading: boolean = false;
	todayDate: Date = new Date();

	readonly dialogRef = inject(MatDialogRef<CreateOrderModalComponent>);
	readonly data = inject(MAT_DIALOG_DATA);

	orderTypes: Selectable<{ id: OrderTypeEnum; name: string }>[] = [
		{ id: OrderTypeEnum.StillLife, name: 'Still-Life', selected: true },
		{ id: OrderTypeEnum.Dressed, name: 'On Model', selected: false },
		{ id: OrderTypeEnum.Complete, name: 'Complete', selected: false }
	];

	constructor(
		private router: Router,
		private utilitiesService: UtilitiesService,
		private graphQLService: GraphQLService,
		private dialog: MatDialog,
		private photographerService: PhotographerFilterService,
		private orderMapper: OrderMapper
	) {}

	ngOnInit(): void {}

	validate(): boolean {
		this.orderValidation.dueDate = this.order?.dueDate ? true : false;
		this.orderValidation.photographer = this.order?.photographer ? true : false;

		return _.every(Object.values(this.orderValidation));
	}

	saveChanges() {
		if (!this.validate()) {
			return;
		}

		let companyOrderInfoInput: any;

		companyOrderInfoInput = {
			statusId: OrderStatus.Draft,
			dueDate: this.order?.dueDate || undefined,
			notes: this.order?.notes || undefined,
			orderTypeId: this.order?.orderType,
			photographerId: this.order?.photographer?.id
		};

		const payload = {
			upsertOrderInfoInput: companyOrderInfoInput
		};

		this.loading = true;

		this.graphQLService.postData(MutationQuery.UPSERT_ORDER_INFO, payload).subscribe({
			next: (value: any) => {
				let createdOrderId = Number(value.data.upsertOrderInfo.message);

				this.graphQLService
					.postData(MutationQuery.ADD_PRODUCTS_TO_ORDER, {
						addproductToOrderInput: {
							orderId: createdOrderId,
							stylesColors: this.data.styles.map((s: Style) => ({
								modelId: s.modelId,
								colorSizes: s.colors ? s.colors.map((c) => ({ colorId: c.id, sizeId: c.size?.id })) : []
							})),
							warehouseId: this.data.warehouse?.id
						}
					})
					.subscribe({
						next: (res: any) => {
							this.loading = false;
							this.dialogRef.close();
							this.utilitiesService.showSnackbar('Order created successfully', SnackbarType.Success);
							this.router.navigate(['/company/order-view/' + createdOrderId + '/order-detail-shots']);
						},
						error: (err) => {
							this.loading = false;
							this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during adding products to order', SnackbarType.Error);
						}
					});
			},
			error: (err) => {
				this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during saving order', SnackbarType.Error);
				this.loading = false;
			}
		});
	}

	openEditPhotographerModal(): void {
		this.dialog
			.open(FilterListComponent<any>, {
				data: {
					title: 'Photographer',
					selected: this.order?.photographer ? [{ ...this.order.photographer, displayId: this.order.photographer.id }] : [],
					filterService: this.photographerService,
					singleSelection: true
				}
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						let photographer = res[0];

						this.order!.photographer = photographer;
					}
				}
			});
	}

	selectOrderType(orderType: Selectable<{ id: OrderTypeEnum; name: string }>) {
		this.orderTypes.forEach((type) => (type.selected = false));
		orderType.selected = true;
		if (this.order) this.order.orderType = orderType.id;
	}

	openSelectedStylesModal() {
		this.dialog
			.open(SelectedStylesModalComponent, {
				data: {
					styles: this.data.styles
				}
			})
			.afterClosed()
			.subscribe({
				next: (res: any) => {}
			});
	}

	close() {
		this.dialogRef.close();
	}
}
